<template>
    <div style="text-align: center;">
        <UserCircleIcon style="width: 60px;" class="mobile-hide" />
        <Timeline :step="step" />
        <p v-if="isDirectSignUp" class="p-mb-3">{{ t("registration.tech.direct.welcome", { fullName: user.fullName }) }}</p>
    </div>
    <div>
        <span class="p-float-label">
            <MultiSelect data-cy="tech-reg-jobtitles"
                         v-model="selectedJobTitles"
                         :filter="true"
                         :autoFilterFocus="true"
                         filterIcon="pi pi-search"
                         :class="{ 'p-invalid': v$.jobTitles.$error && submitted }"
                         :selectionLimit="1"
                         :options="availableJobTitlesOptions"
                         optionLabel="name">
            </MultiSelect>
            <label>{{t("current.job.title")}}</label>
        </span>
        <small class="p-error"
               v-for="(error, index) of v$.jobTitles.$errors"
               :key="index">
            {{ t(error.$message) }}
        </small>
    </div>
    <div class="p-mt-3">
        <span class="p-float-label">
            <TechSkillSelector data-cy="tech-reg-skills"
                               v-model="v$.keySkills.$model"
                               :invalid="v$.keySkills.$error && submitted" />
        </span>
        <small class="p-error"
               v-for="(error, index) of v$.keySkills.$errors"
               :key="index">
            {{ t(error.$message) }}
        </small>
    </div>
    <div class="p-mt-3">
        <span class="p-float-label">
            <Dropdown data-cy="tech-reg-german-level"
                      id="germanLevel"
                      v-model="v$.germanLevel.$model"
                      :options="languageSkills.filter(x => x.id > 1)"
                      :class="{ 'p-invalid': v$.germanLevel.$error && submitted }"
                      optionValue="id"
                      optionLabel="name" />
            <label>{{t("profile.form.languageskill.german")}}</label>
        </span>
        <small class="p-error"
               v-for="(error, index) of v$.germanLevel.$errors"
               :key="index">
            {{ t(error.$message) }}
        </small>
    </div>
    <Documents data-cy="tech-reg-cv-upload"
               :candidate="user"
               :showButtons="false"
               :disableValidation="true"
               :currentBrandId="1"
               :isDirectSignUp="isDirectSignUp"
               :hideConsent="true"
               :showCvIsMandatoryMessage="false"
               @cvUploaded="cvUploaded" />
    <div v-if="isDirectSignUp && !hideTermsOfCondition" style="margin-top: 0; margin-bottom: 40px;">
        <span class="p-float-label terms-and-conditions">
            <Checkbox id="acceptTermsAndConditions"
                      data-cy="termsAndConditionsCheckbox"
                      v-model="v$.acceptTermsAndConditions.$model"
                      :binary="true"
                      :class="{'p-invalid':v$.acceptTermsAndConditions.$error}" />
            <label class="pointerevents no-tooltip"
                   for="termsandconditions">
                {{ t("registration.data.policy.label1") }} <a href="/datenschutz" target="_blank">{{ t("privacy.policy") }}</a>{{ t("registration.data.policy.label2") }}
            </label>
        </span>
        <small v-for="(error, index) of v$.acceptTermsAndConditions.$errors" :key="index" class="p-error p-mt-4" >
            {{ t("registration.data.policy.please.accept") }}
        </small>

    </div>
    <div class="call-to-action mobile-bottom-nav p-mt-0">
        <Button data-cy="tech-reg-button-next"
                class="button-secondary button-next p-pr-3"
                @click="next()">
            {{t("continue")}}
            <i class="pi pi-angle-right" />
        </Button>
    </div>
</template>

<script lang="ts" setup>
    import { ref, onMounted, computed, getCurrentInstance, nextTick } from 'vue';
    import { helpers, requiredIf } from '@vuelidate/validators';
    import { useI18n } from "vue-i18n";
    import { createJobTitleService } from '@/services/jobTitleService';
    import { createSkillService } from '@/services/skillService';
    import { createProfileService } from "@/services/profileService";
    import { createSignupService } from "@/services/signupService";
    import { useToast } from "primevue/usetoast";
    import { Skill } from "@/models/Skill";
    import { JobTitle } from "@/models/JobTitle";
    import Documents from '@/components/profile/Documents.vue'
    import TechSkillSelector from '@/components/TechSkillSelector.vue'
    import useVuelidate from '@vuelidate/core';
    import { UserCircleIcon } from "@heroicons/vue/outline";
    import Timeline from "@/registration/tech/wizard/Timeline.vue";

    const app = getCurrentInstance();
    const props = defineProps(["candidate", "isDirectSignUp", "step", "hideTermsOfCondition" ]);
    const emit = defineEmits(['next']);
    const user = ref(props.candidate);
    const step = ref(props.step);
    const hideTermsOfCondition = ref(props.hideTermsOfCondition);
    const { t } = useI18n();
    const jobTitles = ref<JobTitle>([]);
    const skills = ref<Skill>([]);
    const isDirectSignUp = ref(props.isDirectSignUp);
    const languageSkills = [1, 2, 3, 4].map(x => { return { id: x, name: t('profile.form.languageskill.' + x) }});
    const selectedJobTitles = computed({
        get: () => jobTitles.value.filter(x => user.value.jobTitles.find(j => j.id == x.id)),
        set: (value) => { user.value.jobTitles = value }})


    const rules = computed(() => {
        return {
            jobTitles: { },
            keySkills: { },
            germanLevel: { },
            englishLevel: { },
            acceptTermsAndConditions: {
                requiredIf: helpers.withMessage(t("validate.required.error"), requiredIf(() => {
                    return isDirectSignUp.value && !hideTermsOfCondition.value;
                }))
            }
        }
    });
    const v$ = useVuelidate(rules, user);
    const submitted = ref(false);
    const validate = () => {
        submitted.value = true;
        v$.value.$touch();
        return v$.value.$error;
    }
    function compare(a, b) {
        if (a.name < b.name) {
            return -1;
        }
        if (a.name > b.name) {
            return 1;
        }
        return 0;
    }
    const availableJobTitlesOptions = ref([]);
    const fetchJobTitles = async () => {
        const service = await createJobTitleService();
        const response = await service.getByBrand(1);
        jobTitles.value = response.data;
        availableJobTitlesOptions.value = [...jobTitles.value];
        availableJobTitlesOptions.value.forEach(option => {
            option.name = t(option.name)
        });
        availableJobTitlesOptions.value.sort(compare);
    }
    const fetchSkills = async () => {
        const service = await createSkillService();
        const response = await service.getByBrand(1);
        skills.value = response.data;
    }

    const toaster = useToast();
    const cvUploaded = async () => {
        const service = !isDirectSignUp.value ? await createProfileService(app) : await createSignupService();
        const req = !isDirectSignUp.value ? await service.getLatestCV() : service.getLatestCV(user.value.encryptedId);
        if (req) {
            user.value.latestCV = req;
        } else {
            toaster.add({
                severity: "error",
                summary: "Es gab ein Problem beim Aktualisieren der Ansicht",
                detail: "",
                life: 3000,
            });
        }
    }

    const next = async () => {
        if (validate())
            return;
        emit("next");
    }

    // workaround for enums, which have the int value of 0
    const seniorityChanged = () => {
        nextTick(() => {
            {
                document.getElementById("seniority_label")?.classList.add("p-inputwrapper-filled")
            }
        });
    }

    onMounted(async () => {
        await fetchJobTitles();
        await fetchSkills();
        if (user.value.seniorityLevel == 0)
            seniorityChanged();
    });

</script>

<style>
    .registration-wizard.p-dropdown-panel.p-component.p-ripple-disabled {
        max-width: 400px !important;
    }
    .registration-wizard.p-dropdown-panel .p-dropdown-items .p-dropdown-item {
        white-space: break-spaces;
    }
</style>
